import React, { useState, useEffect, useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box, Container, Typography, Button, Divider, Modal } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import JournalService from '../../services/JournalService';
import LoadingCircle from '../../components/LoadingCircle';
import getCleverTap from '../../utils/clevertap';
import { getMtgPublisherSemiAnnualPrices } from '../../utils/plan';
import Link from 'next/link';
import { EditageConfig } from '../../config/onelogin.config';
import { getMtgContinueUrl } from '../../utils';
import useGeoData from '../../hooks/useGeoData';
const NEXT_PUBLIC_USERS_BUCKET_URL = 'https://users.mindthegraph.com/';

const useStyles = makeStyles((theme) => ({
  // Header Section
  headerSection: {
    paddingTop: 48,
    paddingBottom: 160,
    color: '#fff',
  },
  headerLogo: {
    width: 220,
    marginBottom: 16,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-end',
      flexDirection: 'row',
      justifyContent: 'space-around',
    },
  },
  headerTextSection: {
    maxWidth: 500,
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
  },
  headerTitle: {
    maxWidth: 420,
    fontWeight: 700,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5rem',
    },
  },
  benefitsList: {
    lineHeight: 1.6,
    paddingLeft: 0,
    marginBottom: 32,
    listStylePosition: 'inside',
    '& li': {
      [theme.breakpoints.down('md')]: {
        textAlign: 'left',
      },
    },
  },
  benefitsWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  previewImage: {
    border: '10px solid rgba(255, 255, 255, 0.28)',
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      width: 600,
    },
  },

  // Content Section
  contentSection: {
    backgroundColor: '#eeeeee',
  },
  contentContainer: {
    position: 'relative',
    top: -75,
  },
  infoCard: {
    margin: '0 auto',
    padding: '32px 8px',
    maxWidth: 1000,
    textAlign: 'center',
    borderRadius: 20,
    backgroundColor: '#fff',
    marginBottom: 32,
    '& p': {
      fontFamily: 'Lato, sans-serif',
    },
    '& h4': {
      fontFamily: 'Lato, sans-serif',
      marginBottom: 32,
      [theme.breakpoints.down('md')]: {
        fontSize: 20,
      },
    },
  },
  cardTitle: {
    color: '#424242',
    fontFamily: 'Lato, sans-serif',
  },
  ctaButton: {
    padding: '12px 24px',
    fontWeight: 700,
    borderRadius: 40,
    textTransform: 'none',
    color: '#fff',
    backgroundColor: '#424242',
    '&:hover': {
      backgroundColor: '#212121',
    },
  },
  ctaButtonWrapper: {
    marginTop: 32,
  },

  cta: {
    padding: '12px 100px',
    fontWeight: 700,
    borderRadius: 40,
    textTransform: 'none',
    color: '#424242',
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },

  // Templates Section
  templatesSection: {
    paddingTop: 64,
    paddingBottom: 64,
  },
  templatesSectionTitle: {
    textAlign: 'center',
    marginBottom: 48,
    color: '#424242',
    fontFamily: 'Lato, sans-serif',
    [theme.breakpoints.down('md')]: {
      fontSize: 20,
    },
  },
  templatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3),
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  templateCard: {
    width: '100%',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
  templateImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: 4,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },

  // Footer Section
  footerSection: {
    paddingTop: 32,
    paddingBottom: 32,
    color: '#fff',
  },
  footerContent: {
    textAlign: 'center',
  },
  footerLogoMtg: {
    width: 110,
    maxWidth: '100%',
  },
  footerDivider: {
    margin: '16px 0px',
    backgroundColor: '#9e9e9e',
  },
  footerLogosRow: {
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerLogoCactus: {
    height: 25,
    maxWidth: '100%',
  },
  footerLinks: {
    color: '#bdbdbd',
    marginTop: 16,
    textAlign: 'center',
    '& a': {
      color: 'inherit',
    },
  },

  // Modal styles
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    minWidth: 0,
    padding: 6,
    color: '#424242',
    background: 'transparent',
    '&:hover': {
      background: '#f5f5f5',
    },
  },
  modalContent: {
    position: 'relative', // Add this line
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: '48px 32px',
    maxWidth: 500,
    textAlign: 'center',
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      margin: 16,
      padding: '32px 24px',
    },
  },
  modalTitle: {
    color: '#424242',
    fontFamily: 'Lato, sans-serif',
    marginBottom: 24,
    fontWeight: 600,
  },
  modalButton: {
    marginTop: 32,
    padding: '12px 32px',
    fontWeight: 700,
    borderRadius: 40,
    textTransform: 'none',
    color: '#fff',
    backgroundColor: '#424242',
    '&:hover': {
      backgroundColor: '#212121',
    },
  },
}));

const PublishersPage = () => {
  const classes = useStyles();
  const router = useRouter();
  const { slug } = router.query;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [publisherPlanData, setPublisherPlanData] = useState(null);
  const clevertap = getCleverTap();

  const { geoData, isLoadingGeoData } = useGeoData();

  const getOneLoginLink = useMemo(
    () =>
      (redirectUrl = '/my-creations') => {
        const countryCode = geoData?.country_code || 'US';
        const domain =
          countryCode === 'CN' ? EditageConfig.sso.chineseDomain : EditageConfig.sso.mainDomain;
        return `${domain}/login?publishers=true&continue=${encodeURIComponent(getMtgContinueUrl(redirectUrl))}`;
      },
    [geoData],
  );

  const [pageData, setPageData] = useState({
    backgroundColor: '#01557b',
    headerLogoUrl:
      'https://users.mindthegraph.com/journals/cde9ffb3-06c6-40b8-a36f-388e6b505e31.png',
    previewImageUrl:
      'https://users.mindthegraph.com/journals/5dcbc95a-acfa-4cd7-8456-45a4ab1ddf79.png',
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    JournalService.getPublishersPageData(slug)
      .then((response) => {
        const data = response.data.publishersPageSection;
        const displayData = {
          id: response.data.id,
          ...data,
          headerLogoUrl: `${NEXT_PUBLIC_USERS_BUCKET_URL}journals/${data.headerLogoUrl}`,
          previewImageUrl: `${NEXT_PUBLIC_USERS_BUCKET_URL}journals/${data.previewImageUrl}`,
        };
        setPageData(displayData);
      })
      .catch((error) => {
        console.error('Error fetching publishers page data:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (isLoadingGeoData) return;

    const countryCode = geoData?.country_code || 'US';
    console.log('Fetching publisher plan data for country code:', countryCode);
    getMtgPublisherSemiAnnualPrices(countryCode.toLowerCase())
      .then((data) => setPublisherPlanData(data))
      .catch((error) => console.error('Error fetching publisher plan data:', error));
  }, [isLoadingGeoData, geoData?.country_code]);

  // Format slug to display name (e.g., "journal-of-urology" -> "Journal of Urology")
  const formatSlugToTitle = (slug) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const journalName = formatSlugToTitle(slug);

  const handleTemplateClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const sendCleverTapEvent = (description) => {
    clevertap.event.push('Clicks to view information detail', {
      Product: 'MTG',
      URL: window.location.pathname,
      Description: description,
      Locale: 'publishers',
      Journal: slug,
    });
  };

  const handleSignUp = (description = 'Get Started Now') => {
    sendCleverTapEvent(description);
    window.location.href = getOneLoginLink(
      `/my-creations?journal_id=${encodeURIComponent(slug)}&publishers=true&mode=signup`,
    );
  };

  return (
    <>
      <Head>
        <title>Create your Graphical Abstract for {journalName}</title>
        <meta
          name="description"
          content={`Create professional graphical abstracts for ${journalName}. Get 3x more article downloads, 27x more citations, and 8x more social media shares.`}
        />
      </Head>
      {loading ? (
        <LoadingCircle />
      ) : (
        <Box>
          {/* Header Section */}
          <Box
            className={classes.headerSection}
            style={{ backgroundColor: pageData.backgroundColor }}
          >
            <Container maxWidth="lg">
              <img
                className={classes.headerLogo}
                src={pageData.headerLogoUrl}
                alt="Header logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Box className={classes.headerContent}>
                <Box className={classes.headerTextSection}>
                  <Typography variant="h4" className={classes.headerTitle}>
                    <b>
                      Create your Graphical Abstract for <i>{journalName}</i>
                    </b>
                  </Typography>
                  <Box className={classes.benefitsWrapper}>
                    <ul className={classes.benefitsList}>
                      <b>Graphical Abstracts result in:</b>
                      <li>
                        <b>3x more</b> article downloads
                      </li>
                      <li>
                        <b>27x more citations</b> of your article
                      </li>
                      <li>
                        <b>8x more</b> shares on social media
                      </li>
                    </ul>
                  </Box>
                </Box>
                <Box>
                  <img
                    className={classes.previewImage}
                    src={pageData.previewImageUrl}
                    alt="Graphical Abstract Preview"
                    onError={(e) => {
                      e.target.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400"%3E%3Crect fill="%23ddd" width="600" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="24"%3EPreview Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </Box>
              </Box>
              <Box mt={1}>
                {pageData.id ? (
                  <Link href={getOneLoginLink(`/my-creations?journal_id=${pageData.id}`)} passHref>
                    <Button
                      className={classes.cta}
                      component="a"
                      onClick={() => sendCleverTapEvent('Get Started Now - Journal User')}
                    >
                      Get Started Now
                    </Button>
                  </Link>
                ) : (
                  <Button className={classes.cta} disabled>
                    Get Started Now
                  </Button>
                )}
              </Box>
            </Container>
          </Box>

          {/* Content Section */}
          <Box className={classes.contentSection}>
            <Container maxWidth="lg" className={classes.contentContainer}>
              {/* Free Access Card */}
              <Box className={classes.infoCard}>
                <Typography variant="h4" className={classes.cardTitle}>
                  Free access to explore and create your
                  <br />
                  Graphical Abstract with watermarks
                </Typography>
                <Typography variant="body1">
                  You can start for free and explore all features available at the platform.
                  <br />
                  Journal logos are available in the illustration gallery.
                  <br />
                  But you won&apos;t be able to publish without copyright and watermark removal.
                  <br />
                  To finish your Graphical Abstract, check paid option below.
                </Typography>
              </Box>

              {/* Paid Option Card */}
              <Box className={classes.infoCard}>
                <Typography variant="h4" className={classes.cardTitle}>
                  Remove watermarks and get
                  <br />
                  copyright to publish for{' '}
                  {publisherPlanData?.semiAnnual?.formatted_pack_amount || '29 USD'}
                </Typography>
                <Typography variant="body1">
                  When downloading your creation, you&apos;ll be offered a{' '}
                  {publisherPlanData?.semiAnnual?.formatted_pack_amount || '29 USD'} one-time
                  payment
                  <br />
                  that grants you full copyrights of the Graphical Abstract created and
                  high-resolution
                  <br />
                  download format without any watermarks.
                </Typography>
                <Box className={classes.ctaButtonWrapper}>
                  {pageData.id ? (
                    <Link
                      href={getOneLoginLink(`/my-creations?journal_id=${pageData.id}`)}
                      passHref
                    >
                      <Button
                        className={classes.ctaButton}
                        component="a"
                        onClick={() => sendCleverTapEvent('Get Started Now - Paid Option')}
                      >
                        Get Started Now
                      </Button>
                    </Link>
                  ) : (
                    <Button className={classes.ctaButton} disabled>
                      Get Started Now
                    </Button>
                  )}
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Templates Section */}
          <Box className={classes.templatesSection}>
            <Container maxWidth="lg">
              <Typography variant="h4" className={classes.templatesSectionTitle}>
                Check some of the <b>Graphical Abstracts</b>
                <br />
                templates available for this journal
              </Typography>
              <Box className={classes.templatesGrid}>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <Box key={index} className={classes.templateCard} onClick={handleTemplateClick}>
                    <img
                      src={`https://templates.mindthegraph.com/${slug}-${index}/thumb_1.png`}
                      alt={`Template ${index}`}
                      className={classes.templateImage}
                      onError={(e) => {
                        e.target.style.opacity = '0.5';
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>

          {/* Footer Section */}
          <Box
            className={classes.footerSection}
            style={{ backgroundColor: pageData.backgroundColor }}
          >
            <Container maxWidth="sm">
              <Box className={classes.footerContent}>
                <img
                  className={classes.footerLogoMtg}
                  src="/images/mtg-logo.svg"
                  alt="Mind the Graph Logo"
                />
              </Box>
              <Divider className={classes.footerDivider} />
              <Box className={classes.footerLogosRow}>
                <img
                  className={classes.footerLogoCactus}
                  src="/images/cactus-logo-white.svg"
                  alt="Cactus Logo"
                />
                <Typography variant="body1">
                  © 2026, Cactus Communications. All Rights Reserved
                </Typography>
              </Box>
              <Box className={classes.footerLinks}>
                <a
                  href="https://trust.editage.com/terms-of-use.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  Terms of Use
                </a>
              </Box>
            </Container>
          </Box>

          {/* Template Access Modal */}
          <Modal open={isModalOpen} onClose={handleCloseModal} className={classes.modal}>
            <Box className={classes.modalContent}>
              <Button
                className={classes.modalCloseButton}
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <CloseIcon />
              </Button>
              <Typography variant="h5" className={classes.modalTitle}>
                To access the selected template, <br /> please signup
              </Typography>
              <Button
                className={classes.modalButton}
                onClick={() => handleSignUp('Get Started Now - Template Modal')}
                fullWidth
              >
                Get Started Now
              </Button>
            </Box>
          </Modal>
        </Box>
      )}
    </>
  );
};

export default PublishersPage;
